<template>
  <div class="container case-avatar">
    <transition name="fade" mode="out-in">
      <img
        :key="pictureFor(state.caseConf._avatar)"
        aria-label="Avatar image"
        class="avatar-picture"
        :src="pictureFor(state.caseConf._avatar)"
      >
    </transition>
    <VueSlickCarousel v-bind="slickSettings">
      <div v-for="(avatar, index) in avatars" :key="avatar.id" class="avatar-slide">
        <img
          class="avatar-picture avatar-thumbnail"
          :class="{ 'avatar-thumbnail-selected': index === avatarIndex || index === hoverIndex }"
          :src="pictureFor(avatar)"
          role="button"
          :aria-label="`${avatar.name.en} avatar button`"
          @mouseover="mouseOverImage(index)"
          @mouseout="mouseOutImage"
          @click="selectImage(index)"
        >
        <label v-if="index === avatarIndex || index === hoverIndex">
          {{ avatar.name.en }}
        </label>
      </div>
    </VueSlickCarousel>
    <voice-selector
      :avatar-key="state.caseConf._avatar.key"
      :voice-key="state.caseConf.voiceKey"
      :voices="voices"
      :readonly="readonly"
      @update="updateVoice"
    />
    <outfit-selector
      :user="user"
      :avatar="avatars[avatarIndex]"
      :outfit="state.caseConf.outfit"
      :readonly="readonly"
      @update="updateOutfit"
    />
  </div>
</template>

<script>
import VueSlickCarousel from 'vue-slick-carousel'
import { findIndex, get } from 'lodash';
import defaultPatientPicture from '../CaseSummary/default-patient.png';
import VoiceSelector from '../../common/VoiceSelector.vue'
import OutfitSelector from './OutfitSelector.vue';

export default {
  name: 'case-avatar',
  components: {
    VueSlickCarousel,
    VoiceSelector,
    OutfitSelector,
  },
  props: {
    caseConf: {
      type: Object,
      required: true,
    },
    avatars: {
      type: Array,
      required: true,
    },
    voices: {
      type: Array,
      default: () => [],
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    user: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      slickSettings: {
        arrows: true,
        focusOnSelect: true,
        centerMode: true,
        centerPadding: '20px',
        infinite: true,
        speed: 500,
        slidesToShow: 5,
        slidesToScroll: 1,
        lazyLoad: 'ondemand',
        initialSlide: this.initialAvatarIndex(),
        responsive: [
          {
            breakpoint: 1400,
            settings: {
              slidesToShow: 3,
            }
          },
        ]
      },
      hoverIndex: null,
      state: {
        caseConf: {},
      },
    };
  },
  computed: {
    avatarIndex() {
      if (!this.caseConf) {
        return 0;
      }
      const { _avatar } = this.caseConf;
      if (!_avatar) {
        return 0;
      }
      return findIndex(this.avatars, a => a.id === _avatar.key);
    },
  },
  created() {
    this.state.caseConf = {
      _avatar: this.caseConf._avatar || this.avatars[0],
      voiceKey: this.caseConf.voiceKey,
      outfit: this.caseConf.outfit,
    };
  },
  methods: {
    pictureFor(avatar) {
      return get(avatar, 'picture.url', defaultPatientPicture);
    },
    initialAvatarIndex() {
      if (!this.caseConf) {
        return 0;
      }
      const { _avatar } = this.caseConf;
      if (!_avatar) {
        return 0;
      }
      return findIndex(this.avatars, a => a.id === _avatar.key);
    },
    updateAvatar(avatar) {
      this.state.caseConf._avatar = avatar;
      const isMilitary = (this.user.productType === 'MILITARY');
      const defaultVoice = this.voices.find(voice => voice.avatarKey === avatar.key && voice.isDefault);
      this.state.caseConf.voiceKey = defaultVoice ? defaultVoice.id : null;
      this.state.caseConf.outfit = {
        clothingType: isMilitary ? 'MILITARY_OUTFIT_1': avatar.avatarOptions.defaultOutfit,
        isDirty: false,
        onAtStart: 'CLOTHES_ON',
      };
      this.emitUpdate();
    },
    updateVoice(voice) {
      this.state.caseConf.voiceKey = voice;
      this.emitUpdate();
    },
    updateOutfit(outfit) {
      this.state.caseConf.outfit = outfit;
      this.emitUpdate();
    },
    emitUpdate() {
      this.$emit('update', this.state.caseConf);
    },
    mouseOverImage(slideIndex) {
      this.hoverIndex = slideIndex;
    },
    mouseOutImage() {
      this.hoverIndex = null;
    },
    selectImage(slideIndex) {
      const avatar = this.avatars[slideIndex];
      this.updateAvatar(avatar);
      this.$analytics.event('Authoring Tool', 'patient_select', avatar.name.en);
    },
  },
};
</script>

<style scoped>
.container {
  color: black;
  padding: 8px 0;
}

.avatar-slide {
  text-align: center;
  padding: 10px;
}

.avatar-picture {
  display: block;
  margin: auto;
  box-shadow: 3px 6px 12px rgba(0, 0, 0, 0.4);
  border-radius: 50%;
  width: 100%;
  max-width: 512px;
}

.avatar-thumbnail {
  cursor: pointer;
}

.avatar-thumbnail-selected {
  outline: 2px solid #0C4E86;
}

.fade-enter {
  opacity: 0;
}

.fade-enter-active {
  transition: opacity .20s;
}

.fade-leave {
  opacity: 1;
}

.fade-leave-active {
  transition: opacity .20s;
  opacity: 0;
}

.voice-selector {
  margin-bottom: 10px;
}
</style>

<style>
.slick-prev:before,
.slick-next:before {
  color: black;
}
</style>
